import { useMemo } from 'react';
import { useMarketStore } from '../../stores/marketStore';
import type { FeedLogEntry, FeedOwnership } from '../../stores/marketStore';

interface FeedTapeProps {
  onClose: () => void;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function shortTicker(ticker: string): string {
  if (ticker.startsWith('PM-')) {
    const parts = ticker.split('-');
    return parts.slice(1, 4).join('-');
  }
  const parts = ticker.split('-');
  return parts[parts.length - 1];
}

/** Display name for a feed: the firehose is OG hydra; edges are E1, E2, …
 *  in env-config order (feed ids are `edge:0`, `edge:1`, …). */
function feedLabel(feed: FeedOwnership): string {
  if (feed.role === 'firehose') return 'OG';
  const idx = Number(feed.id.split(':')[1]);
  return Number.isFinite(idx) ? `E${idx + 1}` : 'E?';
}

function TypeBadge({ type }: { type: string }) {
  const styles =
    type === 'market' ? 'bg-green-900/40 text-green-400' :
    type === 'consensus' ? 'bg-amber-900/40 text-amber-300' :
    'bg-yellow-900/40 text-yellow-400';
  const letter =
    type === 'market' ? 'M' :
    type === 'consensus' ? 'C' :
    'B';
  return (
    <span className={`inline-flex w-4 items-center justify-center rounded text-[9px] ${styles}`}>
      {letter}
    </span>
  );
}

// Consensus values render at tenth-of-a-cent grain when the frame carried
// tenths (hydra ≥2026-07-05) — frames emit on tenth-only moves, so showing
// the integer would make consecutive rows look like duplicate no-ops.
// Falls back to the integer cents on pre-rollout frames. Soccer 3-way
// frames append the draw; the ˢ marks Pinnacle-anchored computes (a
// sharp flip alone emits a frame with zero numeric change).
function ConsensusValue({ entry }: { entry: FeedLogEntry }) {
  const fmt = (tenths: number | undefined, cents: number | undefined) =>
    tenths != null ? (tenths / 10).toFixed(1) : String(cents);
  return (
    <span className="text-foreground/90 shrink-0 tabular-nums">
      {fmt(entry.awayProbTenths, entry.awayProbCents)}
      <span className="text-muted-foreground">/</span>
      {fmt(entry.homeProbTenths, entry.homeProbCents)}
      {entry.drawProbTenths != null && (
        <>
          <span className="text-muted-foreground">/</span>
          {(entry.drawProbTenths / 10).toFixed(1)}
        </>
      )}
      {entry.sharpBookUsed === true && (
        <span className="text-amber-300" title="Pinnacle-anchored (sharpBookUsed)">
          ˢ
        </span>
      )}
    </span>
  );
}

function FeedRow({ entry }: { entry: FeedLogEntry }) {
  return (
    <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
      <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
      <TypeBadge type={entry.type} />
      <span className="text-muted-foreground w-6 shrink-0 text-center uppercase">{entry.sport}</span>
      <span className="text-foreground/80 min-w-0 flex-1 truncate">{shortTicker(entry.ticker)}</span>
      {entry.yesBid != null && (
        <span className="text-foreground/90 shrink-0 tabular-nums">
          {entry.yesBid}<span className="text-muted-foreground">/</span>{entry.yesAsk ?? '—'}
        </span>
      )}
      {entry.type === 'market' && (
        <span className={`shrink-0 tabular-nums ${entry.volume != null && entry.volume > 0 ? 'text-cyan-400' : 'text-muted-foreground'}`}>
          {entry.volume != null ? `v${entry.volume}` : 'v?'}
        </span>
      )}
      {entry.type === 'orderbook' && entry.bookLevels != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookLevels}L</span>
      )}
      {entry.type === 'consensus' && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
    </div>
  );
}

/** One server's chip: `E1 NFL` / `OG MLB SOCCER …`. Live ownership — sports
 *  move between chips as the store's arbitration moves them (edge drop/block
 *  hands a sport back to OG). A disconnected edge reads red; claimed-but-not-
 *  served sports render dim (they're on OG until the edge announces them). */
function ServerChip({ feed }: { feed: FeedOwnership }) {
  const down = feed.role === 'edge' && !feed.connected;
  const badge = down
    ? 'bg-red-900/40 text-red-400'
    : feed.role === 'edge'
      ? 'bg-cyan-900/40 text-cyan-300'
      : 'bg-blue-900/40 text-blue-300';
  return (
    <span className="flex min-w-0 items-center gap-1" title={down ? `${feedLabel(feed)} disconnected` : undefined}>
      <span className={`shrink-0 rounded px-1 text-[9px] font-bold ${badge}`}>{feedLabel(feed)}</span>
      <span className="text-foreground/70 min-w-0 truncate text-[9px] uppercase">
        {feed.sports.join(' ')}
        {feed.idleClaims.length > 0 && (
          <span className={`text-muted-foreground/60 ${down ? 'line-through' : ''}`}>
            {feed.sports.length > 0 ? ' ' : ''}{feed.idleClaims.join(' ')}
          </span>
        )}
      </span>
    </span>
  );
}

function FeedColumn({
  title,
  color,
  entries,
  servers,
}: {
  title: string;
  color: string;
  entries: FeedLogEntry[];
  servers: FeedOwnership[];
}) {
  const marketCount = entries.filter(e => e.type === 'market').length;
  const bookCount = entries.filter(e => e.type === 'orderbook').length;
  const consensusCount = entries.filter(e => e.type === 'consensus').length;
  // Unabated only emits consensus today; Kalshi/Poly only emit market+book.
  // Show the relevant subset so the header stays tight.
  const summary = consensusCount > 0 && marketCount === 0 && bookCount === 0
    ? `${consensusCount}C`
    : `${marketCount}M / ${bookCount}B`;

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="border-border border-b px-2 py-1.5">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-bold ${color}`}>{title}</span>
          <span className="text-muted-foreground text-[10px]">{summary}</span>
        </div>
        {servers.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
            {servers.map((feed) => <ServerChip key={feed.id} feed={feed} />)}
          </div>
        )}
      </div>
      <div className="max-h-72 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">Waiting...</div>
        ) : (
          entries.map((entry, i) => <FeedRow key={i} entry={entry} />)
        )}
      </div>
    </div>
  );
}

export function FeedTape({ onClose }: FeedTapeProps) {
  const getFeedLog = useMarketStore((s) => s.getFeedLog);
  const getFeedOwnership = useMarketStore((s) => s.getFeedOwnership);
  const version = useMarketStore((s) => s.version);

  const kalshiEntries = useMemo(() => {
    void version;
    return getFeedLog('kalshi');
  }, [getFeedLog, version]);

  const polyEntries = useMemo(() => {
    void version;
    return getFeedLog('polymarket');
  }, [getFeedLog, version]);

  const unabatedEntries = useMemo(() => {
    void version;
    return getFeedLog('unabated');
  }, [getFeedLog, version]);

  // Live sport ownership (edge preferred / firehose fallback) for the server
  // chips. Kalshi/Poly market+book frames follow this partition; Unabated
  // consensus is ungated and comes from OG regardless of sport ownership, so
  // its column pins a bare OG chip.
  const ownership = useMemo(() => {
    void version;
    return getFeedOwnership();
  }, [getFeedOwnership, version]);

  const unabatedServers = useMemo(
    () => ownership
      .filter((f) => f.role === 'firehose')
      .map((f) => ({ ...f, sports: [], idleClaims: [] })),
    [ownership],
  );

  return (
    <div className="bg-background/95 border-border absolute bottom-full right-0 z-50 mb-1 w-[1180px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border shadow-2xl">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-foreground text-xs font-semibold">Live Feed</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground px-1 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Live streams */}
      <div className="divide-border flex divide-x">
        <FeedColumn title="Kalshi" color="text-blue-400" entries={kalshiEntries} servers={ownership} />
        <FeedColumn title="Polymarket" color="text-purple-400" entries={polyEntries} servers={ownership} />
        <FeedColumn title="Unabated" color="text-amber-300" entries={unabatedEntries} servers={unabatedServers} />
      </div>
    </div>
  );
}
